
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X, Calendar, Settings, FileText, LogOut, GraduationCap, ShoppingBag, User, HelpCircle, HandHelping, BadgeHelp, CircleHelp, MessagesSquare } from 'lucide-react';
import { NotificationBell } from '@/components/NotificationBell';
import { AccountSwitcher } from '@/components/AccountSwitcher';
import { cn } from '@/lib/utils';
import SpotlightWizard from '@/components/SpotlightWizard';
import WelcomeWizardModal from '@/components/WelcomeWizardModal';

export function Navbar() {
  const { t, locale } = useLanguage() as any;
  const { isAuthenticated, logout, user } = useAuth();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Wizard state listener — drives Settings highlight + welcome modal
  useEffect(() => {
    const evaluateWizard = () => {
      if (!isAuthenticated) {
        setShowWelcomeModal(false);
        setShowWizard(false);
        return;
      }
      if (user?.is_wizard_disable) {
        setShowWelcomeModal(false);
        setShowWizard(false);
        return;
      }
      const active = localStorage.getItem('wizard-active') === 'true';
      const step = localStorage.getItem('wizard-step');
      const welcomeDismissed = localStorage.getItem('wizard-welcome-dismissed') === 'true' || (user.hasCreatedCategory || user.hasCreatedStudent || user.hasCreatedEvent);

      if (active && (step === '0') && (!user.hasCreatedCategory || !user.hasCreatedStudent)) {
        // Show welcome modal once if not dismissed
        if (!welcomeDismissed && user && !user.hasCreatedEvent) {
          setShowWelcomeModal(true);
          setShowWizard(false);
        } else {
          setShowWelcomeModal(false);
          setShowWizard(true);
        }
      } else {
        setShowWelcomeModal(false);
        setShowWizard(false);
      }


      //just on agenda page
      if (active && (step === '3') && window.location.pathname === '/agenda' && (!user.hasCreatedCategory || !user.hasCreatedStudent)) {
        setShowWizard(true);
      } 



    };
    evaluateWizard();
    window.addEventListener('wizard-state-changed', evaluateWizard);
    window.addEventListener('storage', evaluateWizard);
    return () => {
      window.removeEventListener('wizard-state-changed', evaluateWizard);
      window.removeEventListener('storage', evaluateWizard);
    };
  }, [isAuthenticated, user?.is_wizard_disable]);

  const dismissWelcome = () => {
    localStorage.setItem('wizard-welcome-dismissed', 'true');
    localStorage.setItem('wizard-active', 'false');
    setShowWelcomeModal(false);
    // Show the spotlight on Settings link after welcome closes
    if (localStorage.getItem('wizard-active') === 'true' &&
        localStorage.getItem('wizard-step') === '0') {
        setShowWizard(true);
    }
  };

   const dismissOnOpen = () => {
    localStorage.setItem('wizard-welcome-dismissed', 'true');
    setShowWelcomeModal(false);
    // Show the spotlight on Settings link after welcome closes
    if (localStorage.getItem('wizard-active') === 'true' &&
        localStorage.getItem('wizard-step') === '0') {
        setShowWizard(true);
    }
  };

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
  };

  const handleNavClick = () => {
    // Any navigation click dismisses the welcome modal permanently
    if (!localStorage.getItem('wizard-welcome-dismissed')) {
      localStorage.setItem('wizard-welcome-dismissed', 'true');
    }
    setShowWelcomeModal(false);
  };

  const handleSettingsClick = () => {
    handleNavClick();
    if (localStorage.getItem('wizard-active') === 'true' &&
        localStorage.getItem('wizard-step') === '0') {
      localStorage.setItem('wizard-step', '1');
      window.dispatchEvent(new Event('wizard-state-changed'));
    }
    setShowWizard(false);
  };

  const logoDestination = isAuthenticated ? "/agenda" : "/";

  const isStudent = user?.account_type === "Student";
  const isParent = user?.account_type === "Parent";
  const isSchool = user?.account_type === "S";
  const isCoach = user?.account_type === "C";
  const isClient = isStudent || isParent || (user && !isSchool && !isCoach);

  return (
    <header
      className={cn(
        "fixed top-0 w-full z-50 transition-all duration-200 py-3",
        isScrolled
          ? "bg-white/80 backdrop-blur-lg shadow-sm"
          : "bg-transparent"
      )}
    >
      <div className="container flex items-center justify-between">
        <Link
  to={logoDestination}
  onClick={handleNavClick}
  className="font-display text-3xl font-bold text-foreground flex items-center justify-between"
>
  {/* Style injecté localement avec animation rapide */}
  <style>{`
    @keyframes shakeAppear {
      0% {
        opacity: 0;
        transform: translateY(8px) rotate(-10deg);
      }
      50% {
        opacity: 1;
        transform: translateY(-4px) rotate(5deg);
      }
      70% {
        transform: translateY(2px) rotate(-3deg);
      }
      100% {
        opacity: 1;
        transform: translateY(0) rotate(0deg);
      }
    }
    .letter-shake {
      animation: shakeAppear 0.25s ease-out forwards;
    }
  `}</style>

  <img src="https://storage.googleapis.com/sportlogin/frontend/logo-blue.png" width="55" alt="SportLogin" /> 
  
  {/* ml-1.5 pour coller davantage le texte au logo */}
  <span className="inline-block md:hidden lg:inline-block text-2xl font-bold ml-1.5 transition-opacity hover:opacity-80">
    {[
      { l: 'S', d: '0ms' },
      { l: 'P', d: '35ms' },
      { l: 'O', d: '70ms' },
      { l: 'R', d: '105ms' },
      { l: 'T', d: '140ms' },
      { l: 'L', d: '175ms' },
      { l: 'O', d: '210ms' },
      { l: 'G', d: '245ms' },
      { l: 'I', d: '280ms' },
      { l: 'N', d: '315ms' }
    ].map((item, index) => (
      <span
        key={index}
        style={{ animationDelay: item.d }}
        /* Dégradé encore plus sombre et intense */
        className="letter-shake inline-block opacity-0 bg-gradient-to-b from-[#0f5b8f] to-[#4ba6dd] bg-clip-text text-transparent"
      >
        {item.l}
      </span>
    ))}
  </span>
</Link>

        <nav className="hidden md:flex items-center space-x-1">
          {isAuthenticated ? (
            <>
              <Link to="/agenda" onClick={handleNavClick} className="px-4 py-2 flex items-center text-foreground/80 hover:text-foreground font-medium transition-colors">
                <Calendar size={18} className="mr-1" />
                {t('nav.agenda')}
              </Link>
              <Link to="/invoices" onClick={handleNavClick} className="px-4 py-2 flex items-center text-foreground/80 hover:text-foreground font-medium transition-colors">
                <FileText size={18} className="mr-1" />
                {t('nav.invoices')}
              </Link>
             
              {isClient && (
                <Link to="/shop" onClick={handleNavClick} className="px-4 py-2 flex items-center text-foreground/80 hover:text-foreground font-medium transition-colors">
                  <ShoppingBag size={18} className="mr-1" />
                  {t('shop.title')}
                </Link>
              )}
              {!isStudent && !isParent && (
                <Link to="/settings" id="navbar-settings-link" onClick={handleSettingsClick} className="px-4 py-2 flex items-center text-foreground/80 hover:text-foreground font-medium transition-colors">
                  <Settings size={18} className="mr-1" />
                  {t('nav.settings')}
                </Link>
              )}

              <div className="pl-1 flex items-center space-x-2">
                <NotificationBell />
                <LanguageSwitcher />
                <AccountSwitcher />
              </div>
            </>
          ) : (
            <>
              <Link to="/" className="px-4 py-2 text-foreground/80 hover:text-foreground font-medium transition-colors">
                {t('nav.home')}
              </Link>
              <a href="#features" className="px-4 py-2 text-foreground/80 hover:text-foreground font-medium transition-colors">
                {t('nav.features')}
              </a>

              <div className="pl-4 flex items-center space-x-4">
                <LanguageSwitcher />
                <Link to="/login">
                  <Button variant="secondary" size="sm">
                    {t('common.login')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button variant="default" size="sm">
                    {t('common.register')}
                  </Button>
                </Link>
              </div>
            </>
          )}
        </nav>

        <div className="flex items-center md:hidden">
          <LanguageSwitcher />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </Button>
        </div>
      </div>

      <div
        className={cn(
          "md:hidden fixed inset-x-0 top-16 bg-white backdrop-blur-lg transition-all duration-300 transform ease-in-out shadow-md",
          isMenuOpen ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 pointer-events-none"
        )}
      >
        <div className="container py-4 flex flex-col space-y-2">
          {isAuthenticated ? (
            <>
              {isClient && (
                <Link
                  to="/shop"
                  className="px-4 py-3 hover:bg-secondary rounded-md font-medium flex items-center"
                  onClick={() => { handleNavClick(); setIsMenuOpen(false); }}
                >
                  <ShoppingBag size={18} className="mr-2" />
                  {t('shop.title')}
                </Link>
              )}

              <div className="border-t pt-3 mt-2">
                <AccountSwitcher isMobile onMenuClose={() => setIsMenuOpen(false)} />
              </div>

              <div className="border-t pt-3 mt-2 flex items-center px-4 py-3"
                onClick={() => navigate('/my-account')}
              >
                <User size={16} className="mr-2" />
                <span className="text-sm">{t('nav.myAccount')}</span>
              </div>

              <div className="border-t pt-3 mt-2 flex items-center px-4 py-3"
                onClick={() => navigate('/messages')}
              > 
                <MessagesSquare size={16} className="mr-2" />
                <span className="text-sm">{String(locale || 'fr').startsWith('fr') ? 'Messagerie' : 'Messages'}</span>
              </div>

              {!isStudent && !isParent && (
                <div className="border-t pt-3 mt-2 flex items-center px-4 py-3"
                  onClick={() => navigate('/contact-support')}
                >
                  <CircleHelp size={16} className="mr-2" />
                  <span className="text-sm">{t('nav.support')}</span>
                </div>
              )}

              <div className="border-t pt-3 mt-2">
                <button
                  className="w-full px-4 py-3 hover:bg-secondary rounded-md font-medium flex items-center text-destructive"
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                >
                  <LogOut size={18} className="mr-2" />
                  {t('login.logout')}
                </button>
              </div>
            </>
          ) : (
            <>
              <Link
                to="/"
                className="px-4 py-3 hover:bg-secondary rounded-md font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.home')}
              </Link>
              <a
                href="#features"
                className="px-4 py-3 hover:bg-secondary rounded-md font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.features')}
              </a>
              <Link
                to="/contact"
                className="px-4 py-3 hover:bg-secondary rounded-md font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.contact')}
              </Link>

              <div className="flex space-x-2 pt-2">
                <Link to="/login" className="flex-1" onClick={() => setIsMenuOpen(false)}>
                  <Button variant="outline" className="w-full">
                    {t('common.login')}
                  </Button>
                </Link>
                <Link to="/register" className="flex-1" onClick={() => setIsMenuOpen(false)}>
                  <Button variant="default" className="w-full">
                    {t('common.register')}
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Welcome modal — first visit only */}
      <WelcomeWizardModal
        isOpen={showWelcomeModal}
        onClose={dismissWelcome}
        onStart={dismissOnOpen}
      />

      {/* Spotlight on Settings link — desktop navbar OR mobile bottom nav */}
      <SpotlightWizard
        isVisible={showWizard}
        targetSelector={isMobileViewport ? '#mobile-bottom-settings-link' : '#navbar-settings-link'}
        message={t('wizard.settings') || "Cliquez sur Paramètres pour configurer vos catégories et vos élèves."}
        position={isMobileViewport ? 'top' : 'bottom'}
        showCloseButton={false}
        closeOnTargetClick={true}
        onClose={() => {
          setShowWizard(false);
          handleSettingsClick();
        }}
      />
    </header>
  );
}
